/**
 * RestaurantCard Component
 * Displays a single restaurant with details and booking options
 */

import { useState } from 'react';
import { MapPin, Star, DollarSign, Users, ChefHat, Phone, ExternalLink } from 'lucide-react';
import { isFeatureEnabled } from '../config/features';
import { OPENTABLE_CONFIG } from '../config/opentable';
import { generateAffiliateLink, trackReservationClick } from '../services/affiliateService';
import ReservationBooking from './ReservationBooking';
import UberButton from './UberButton';

// ── Helpers ───────────────────────────────────────────────────────────────────

const isOpenTableSource = (dataSource) =>
  dataSource === 'opentable-live' || dataSource === 'opentable-cache';

const isAISource = (dataSource) => dataSource === 'ai-fallback';

const getPriceRangeDisplay = (priceRange) => priceRange || '$';

const formatDistance = (distance) => {
  if (!distance) return '';
  if (distance < 1) {
    return `${Math.round(distance * 5280)} ft`;
  }
  return `${distance.toFixed(1)} mi`;
};

// ── Sub-components ────────────────────────────────────────────────────────────

const RestaurantImage = ({ imageUrl, name }) => {
  if (!imageUrl) return null;
  return (
    <div style={{
      width: '100%', height: '200px', borderRadius: '6px',
      overflow: 'hidden', marginBottom: '12px', backgroundColor: '#f3f4f6'
    }}>
      <img
        src={imageUrl}
        alt={name}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    </div>
  );
};

const RestaurantHeader = ({ name, rating, reviewCount }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
      {name}
    </h3>
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <Star size={16} fill="#FFD700" color="#FFD700" />
      <span style={{ fontSize: '14px', fontWeight: '500' }}>
        {rating?.toFixed(1) || 'N/A'}
      </span>
      {reviewCount && (
        <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>
          ({reviewCount})
        </span>
      )}
    </div>
  </div>
);

const CuisineAndPrice = ({ cuisine, priceRange, distance }) => (
  <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', alignItems: 'center' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#6b7280' }}>
      <ChefHat size={14} />
      <span>{cuisine}</span>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#6b7280' }}>
      <DollarSign size={14} />
      <span>{getPriceRangeDisplay(priceRange)}</span>
    </div>
    {distance && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#6b7280' }}>
        <MapPin size={14} />
        <span>{formatDistance(distance)}</span>
      </div>
    )}
  </div>
);

const TerminalGateInfo = ({ terminal, gate }) => {
  if (!terminal && !gate) return null;
  return (
    <div style={{ fontSize: '13px', color: '#059669', marginBottom: '8px', fontWeight: '500' }}>
      {terminal && `Terminal ${terminal}`}
      {terminal && gate && ' • '}
      {gate && `Gate ${gate}`}
    </div>
  );
};

const PhoneLink = ({ phone }) => {
  if (!phone) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', fontSize: '13px', color: '#6b7280' }}>
      <Phone size={14} />
      <a href={`tel:${phone}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
        {phone}
      </a>
    </div>
  );
};

const ReserveButton = ({ openTableSourced, onReserveClick }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    {openTableSourced && (
      <img
        src={OPENTABLE_CONFIG.branding.logoPath}
        alt="OpenTable"
        width={OPENTABLE_CONFIG.branding.logoSize}
        height={OPENTABLE_CONFIG.branding.logoSize}
        style={{ flexShrink: 0 }}
      />
    )}
    <button
      onClick={onReserveClick}
      style={{
        flex: 1, padding: '10px 16px', backgroundColor: '#d32f2f', color: '#ffffff',
        border: 'none', borderRadius: '6px', fontSize: '15px', fontWeight: '600',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: '8px', transition: 'background-color 0.2s'
      }}
      onMouseEnter={(e) => { e.target.style.backgroundColor = '#b71c1c'; }}
      onMouseLeave={(e) => { e.target.style.backgroundColor = '#d32f2f'; }}
    >
      <Users size={16} />
      Reserve a Table
    </button>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const RestaurantCard = ({ restaurant, trip, userProfile, onReservationCreated }) => {
  const [showBookingModal, setShowBookingModal] = useState(false);

  if (!isFeatureEnabled('OPENTABLE_ENABLED')) {
    return null;
  }

  const openTableSourced = isOpenTableSource(restaurant.dataSource);
  const aiSourced = isAISource(restaurant.dataSource);

  const handleReserveClick = () => {
    if (openTableSourced && restaurant.reservationUrl) {
      const affiliateLink = generateAffiliateLink(restaurant.reservationUrl, {
        userId: userProfile?.userId,
        tripId: trip?.tripId,
        restaurantId: restaurant.id || restaurant.openTableId,
      });

      trackReservationClick({
        userId: userProfile?.userId,
        tripId: trip?.tripId,
        restaurantId: restaurant.id || restaurant.openTableId,
        restaurantName: restaurant.name,
        destinationCity: trip?.destination,
        linkUrl: affiliateLink,
      });
    }

    setShowBookingModal(true);
  };

  const handleReservationCreated = (reservation) => {
    setShowBookingModal(false);
    if (onReservationCreated) {
      onReservationCreated(reservation);
    }
  };

  return (
    <>
      <div style={{
        border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px',
        marginBottom: '16px', backgroundColor: '#ffffff',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <RestaurantImage imageUrl={restaurant.imageUrl} name={restaurant.name} />
        <RestaurantHeader name={restaurant.name} rating={restaurant.rating} reviewCount={restaurant.reviewCount} />
        <CuisineAndPrice cuisine={restaurant.cuisine} priceRange={restaurant.priceRange} distance={restaurant.distance} />

        {/* Description */}
        {restaurant.description && (
          <p style={{ fontSize: '14px', color: '#4b5563', margin: '8px 0', lineHeight: '1.5' }}>
            {restaurant.description}
          </p>
        )}

        {/* Address */}
        {restaurant.address && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '8px', fontSize: '13px', color: '#6b7280' }}>
            <MapPin size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{restaurant.address}</span>
          </div>
        )}

        {/* Request Uber Button */}
        {restaurant.address && (
          <div style={{ marginBottom: '12px' }}>
            <UberButton destination={restaurant.address} destinationName={restaurant.name} />
          </div>
        )}

        <TerminalGateInfo terminal={restaurant.terminal} gate={restaurant.gate} />
        <PhoneLink phone={restaurant.phone} />

        {/* AI Recommended Badge */}
        {aiSourced && (
          <div style={{
            display: 'inline-block', padding: '2px 8px', backgroundColor: '#ede9fe',
            color: '#7c3aed', borderRadius: '4px', fontSize: '12px', fontWeight: '500', marginBottom: '8px'
          }}>
            AI Recommended
          </div>
        )}

        <ReserveButton openTableSourced={openTableSourced} onReserveClick={handleReserveClick} />

        {/* Reservation link */}
        {restaurant.reservationUrl && openTableSourced && (
          <div style={{ textAlign: 'center', marginTop: '6px' }}>
            <a
              href={restaurant.reservationUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '13px', color: '#2563eb', textDecoration: 'none' }}
            >
              <ExternalLink size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
              View on OpenTable
            </a>
          </div>
        )}

        {/* OpenTable Branding */}
        {openTableSourced && (
          <div style={{
            fontSize: `${Math.max(OPENTABLE_CONFIG.branding.minTaglineFontSize, 11)}px`,
            color: '#9ca3af', textAlign: 'center', marginTop: '8px'
          }}>
            {OPENTABLE_CONFIG.branding.tagline}
          </div>
        )}
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <ReservationBooking
          restaurant={restaurant}
          trip={trip}
          userProfile={userProfile}
          onClose={() => setShowBookingModal(false)}
          onReservationCreated={handleReservationCreated}
        />
      )}
    </>
  );
};

export default RestaurantCard;
